import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, Calendar, Users, Heart, Code, ChevronDown, CheckCircle, Video, Mail } from "lucide-react";
import AIAssistant from "@/components/AIAssistant";
import EventbriteRegistrationFlow, { RegistrationFlowResult } from "@/components/EventbriteRegistrationFlow";
import LivestreamRegistrationForm from "@/components/LivestreamRegistrationForm";
import ContactFormDialog from "@/components/ContactFormDialog";
import type { PageSection } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";

const About: React.FC = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState<PageSection[]>([]);
  const [aiPrompt, setAiPrompt] = useState("");
  const [isAiOpen, setIsAiOpen] = useState(false);
  const [aiPromptKey, setAiPromptKey] = useState(0);
  const [showLivestreamThankYou, setShowLivestreamThankYou] = useState(false);
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<RegistrationFlowResult | null>(null);
  const [formMode, setFormMode] = useState<"in-person" | "livestream">("in-person");
  const [contactOpen, setContactOpen] = useState(false);
  const [heroVideo, setHeroVideo] = useState({
    url: "https://youtu.be/CpjGwzS31wg",
    autoplay: true,
    muted: true,
    loop: true,
    controls: false
  });

  useEffect(() => {
    loadPageContent();
    loadSettings();
  }, []);

  const loadPageContent = async () => {
    const { data, error } = await supabase
      .from('page_sections')
      .select('*')
      .eq('page_id', 'home')
      .order('order_num');

    if (data && !error) {
      setSections(data);
    }
  };

  const loadSettings = async () => {
    const { data, error } = await supabase
      .from('settings')
      .select('key, value')
      .in('key', [
        'eventbrite_url',
        'HERO_VIDEO_URL',
        'HERO_VIDEO_AUTOPLAY',
        'HERO_VIDEO_MUTED',
        'HERO_VIDEO_LOOP',
        'HERO_VIDEO_CONTROLS'
      ]);

    if (data && !error) {
      const settings: Record<string, string> = {};
      data.forEach(item => settings[item.key] = item.value);
      
      if (settings.HERO_VIDEO_URL) {
        setHeroVideo({
          url: settings.HERO_VIDEO_URL,
          autoplay: settings.HERO_VIDEO_AUTOPLAY === 'true',
          muted: settings.HERO_VIDEO_MUTED === 'true',
          loop: settings.HERO_VIDEO_LOOP === 'true',
          controls: settings.HERO_VIDEO_CONTROLS === 'true'
        });
      }
    }
  };

  const getSection = (id: string) => sections.find(s => s.section_id === id);

  const handleHeroAiSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (aiPrompt.trim()) {
      setIsAiOpen(true);
      setAiPromptKey(prev => prev + 1);
    }
  };

  const scrollToRegistrationForm = (e: React.MouseEvent) => {
    e.preventDefault();
    const formSection = document.getElementById('register-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLivestreamSuccess = () => {
    setShowLivestreamThankYou(true);
    const thankYouSection = document.getElementById('thank-you');
    if (thankYouSection) {
      thankYouSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleRegistrationComplete = (result: RegistrationFlowResult) => {
    setRegistrationResult(result);
    setShowRegistrationSuccess(true);
    
    if (result.isTeamParticipant) {
      // Redirect to challenges page to pick a team
      toast.success("Registration complete! Now let's find you a team.");
      navigate("/challenges", {
        state: {
          openJoinFlow: true,
          registrationId: result.registrationId,
        }
      });
    } else {
      // Show success message
      document.getElementById('thank-you')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const hero = getSection("hero");
  const about = getSection("about");
  const registration = getSection("registration");
  const experience = getSection("experience");

  const faqs = [
    { 
      q: "Do I need AI experience?", 
      a: "Some AI experience is preferred for team participants, but attendees need no AI experience. Our goal is to teach everyone through training tables, mentors, and hands-on learning." 
    },
    { 
      q: "Can kids attend?", 
      a: "Yes! BuildWithChrist.ai is family-friendly. We have an indoor jungle gym and supervised play area staffed by licensed childcare providers. You can indicate interest in kids' AI activities during registration." 
    },
    { 
      q: "What's the schedule?", 
      a: "Doors open at 11am with networking and setup. The main build event runs from 12pm to 4pm, including team intros, live building sessions, networking breaks, lightning talks, and ending with team showcases and awards."
    },
    { 
      q: "How do teams work?", 
      a: "Teams work on real nonprofit use cases with coaching and mentorship. You can join as part of a team or apply as an individual and we'll help match you on event day. Training tables are available for beginners." 
    },
    { 
      q: "What should I bring?", 
      a: "Just bring your laptop and enthusiasm. We'll provide everything else including food, tools, and learning materials." 
    },
    { 
      q: "How do sponsors get involved?", 
      a: "Sponsors provide financial support for production, tools, and nonprofits while gaining brand exposure and connecting with values-aligned talent. Contact us or download the sponsor overview for tier details." 
    },
    { 
      q: "Will food be provided?", 
      a: "Yes, meals and refreshments will be provided throughout the day so you can focus on building, learning, and connecting." 
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  const location = useLocation();
  const canonicalUrl = `https://buildwithchrist.ai${location.pathname}`;

  return (
    <div className="relative">
      <Helmet>
        <title>About | BuildWithChrist.ai</title>
        <meta name="description" content="Learn about BuildWithChrist.ai - an event rooted in Christian values that welcomes all people of goodwill to learn and apply AI as instruments of service." />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <ContactFormDialog open={contactOpen} onOpenChange={setContactOpen} />
      
      <AIAssistant 
        key={aiPromptKey} 
        initialPrompt={aiPrompt} 
        isOpen={isAiOpen} 
        onClose={() => {
          setIsAiOpen(false);
          setAiPrompt("");
        }} 
      />

      {/* Hero Section */}
      <section id="top" className="min-h-screen pt-2 flex items-center relative overflow-hidden">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px]" />
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
          <div className="space-y-8">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight font-display">
              <span className="text-gradient">
                Artificial Intelligence, Real Impact
              </span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
              Learn AI by building solutions for virtuous causes.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="text-lg" onClick={scrollToRegistrationForm}>
                Register as Attendee
              </Button>
              <Button size="lg" variant="outline" className="text-lg" onClick={scrollToRegistrationForm}>
                Join a Team
              </Button>
            </div>
            <div className="mt-8 bg-card border border-border p-1 rounded-lg shadow-xl max-w-md">
              <form onSubmit={handleHeroAiSubmit} className="flex">
                <Input
                  type="text"
                  placeholder="Ask me anything about the event..."
                  className="flex-grow border-0 focus-visible:ring-0"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                />
                <Button type="submit" size="icon" variant="ghost">
                  <ArrowRight size={20} />
                </Button>
              </form>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative w-full h-[400px] rounded-2xl border border-border overflow-hidden">
              {heroVideo.url && (
                <iframe
                  src={(() => {
                    // Extract video ID from various YouTube URL formats
                    const url = heroVideo.url;
                    let videoId = '';
                    if (url.includes('youtu.be/')) {
                      videoId = url.split('youtu.be/')[1]?.split('?')[0] || '';
                    } else if (url.includes('youtube.com/watch')) {
                      const urlParams = new URLSearchParams(url.split('?')[1]);
                      videoId = urlParams.get('v') || '';
                    } else if (url.includes('youtube.com/embed/')) {
                      videoId = url.split('embed/')[1]?.split('?')[0] || '';
                    }
                    
                    const params = new URLSearchParams();
                    if (heroVideo.autoplay) params.set('autoplay', '1');
                    if (heroVideo.muted) params.set('mute', '1');
                    if (heroVideo.loop) {
                      params.set('loop', '1');
                      params.set('playlist', videoId);
                    }
                    params.set('controls', heroVideo.controls ? '1' : '0');
                    params.set('showinfo', '0');
                    params.set('rel', '0');
                    params.set('modestbranding', '1');
                    
                    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
                  })()}
                  title="BuildWithChrist.ai Event Video"
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  frameBorder="0"
                />
              )}
              {!heroVideo.url && (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20" />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form Section */}
      <section id="register-form" className="py-16 px-4 bg-card/50">
        <div className="max-w-6xl mx-auto">
          {showLivestreamThankYou ? (
            <div id="thank-you" className="text-center space-y-6 py-12 max-w-xl mx-auto">
              <div className="mx-auto w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-secondary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold font-display">
                Livestream Registration Received!
              </h2>
              <p className="text-lg text-muted-foreground">
                We'll send you the livestream link and details before the event. See you there!
              </p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowLivestreamThankYou(false);
                  setFormMode("in-person");
                }}
                size="sm"
              >
              Register Another Person
            </Button>
          </div>
        ) : showRegistrationSuccess && !registrationResult?.isTeamParticipant ? (
          <div id="thank-you" className="text-center space-y-6 py-12 max-w-xl mx-auto">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-display">
              Registration Complete!
            </h2>
            <p className="text-lg text-muted-foreground">
              You're all set for the event. We'll send you details as the date approaches.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate("/dashboard")}>
                Go to Dashboard
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowRegistrationSuccess(false);
                  setRegistrationResult(null);
                }}
              >
                Register Another Person
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div className="space-y-4 lg:sticky lg:top-24">
              <h2 className="text-2xl md:text-3xl font-bold font-display">
                {formMode === "in-person" ? "Register for the Event" : "Join the Livestream"}
              </h2>
              <p className="text-muted-foreground">
                {formMode === "in-person" 
                  ? "Join us for a day of building, learning, and connecting with mission-driven builders."
                  : "Watch teams build, learn from lightning talks, and be part of the community from anywhere."}
              </p>
              {formMode === "in-person" && (
                <div className="hidden lg:block space-y-3 pt-4 border-t border-border">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span>One full day of hands-on AI learning</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Users className="w-4 h-4 text-primary" />
                    <span>Network with mission-driven peers</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Heart className="w-4 h-4 text-primary" />
                    <span>Build solutions for virtuous causes</span>
                  </div>
                </div>
              )}
              
              {/* Toggle callout */}
              <div className="mt-6 p-4 rounded-lg bg-muted/50 border border-border">
                {formMode === "in-person" ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Can't attend in person?</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setFormMode("livestream")}
                      className="gap-2"
                    >
                      <Video className="w-4 h-4" />
                      Join Virtually
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">Want to attend in person?</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setFormMode("in-person")}
                      className="gap-2"
                    >
                      <Users className="w-4 h-4" />
                      Register In Person
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div>
              {formMode === "in-person" ? (
                <EventbriteRegistrationFlow
                  onComplete={handleRegistrationComplete}
                  showEventbriteFirst={false}
                />
              ) : (
                <LivestreamRegistrationForm onSuccess={handleLivestreamSuccess} />
              )}
            </div>
          </div>
        )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-5xl font-bold font-display">What is BuildWithChrist.ai?</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            An event rooted in Christian values that welcomes all people of goodwill to learn and apply AI as instruments of service.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="bg-card border border-border p-6 rounded-xl">
              <div className="p-3 bg-primary/10 text-primary rounded-full w-fit mx-auto mb-4">
                <Heart size={32} />
              </div>
              <h3 className="font-bold text-lg mb-2">AI for Human Flourishing</h3>
              <p className="text-sm text-muted-foreground">We use AI to deepen human relationships, not replace them.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <div className="p-3 bg-secondary/10 text-secondary rounded-full w-fit mx-auto mb-4">
                <Users size={32} />
              </div>
              <h3 className="font-bold text-lg mb-2">Story-Driven Solutions</h3>
              <p className="text-sm text-muted-foreground">Every build is tied to a real person, nonprofit, or story.</p>
            </div>
            <div className="bg-card border border-border p-6 rounded-xl">
              <div className="p-3 bg-primary/10 text-primary rounded-full w-fit mx-auto mb-4">
                <Code size={32} />
              </div>
              <h3 className="font-bold text-lg mb-2">Learn by Doing</h3>
              <p className="text-sm text-muted-foreground">You'll learn AI hands-on, building real prototypes that matter.</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <span>
              Brought to you by <a href="https://cityonpurpose.com?utm_source=buildwithchrist&utm_medium=website&utm_campaign=bwc_about&utm_content=about_page" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold">City on Purpose</a>
            </span>
            <Button variant="outline" size="sm" onClick={() => setContactOpen(true)} className="gap-2">
              <Mail size={16} />
              Contact Us
            </Button>
          </p>
        </div>
      </section>

      {/* Registration Section */}
      <section id="registration" className="py-20 px-4 bg-card/30">
        <div className="max-w-5xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-5xl font-bold font-display">How Do You Want to Join Us?</h2>
          <p className="text-xl text-muted-foreground">
            Whether you want to build, learn, support, or just watch, there's a place for you at BuildWithChrist.ai.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="bg-card border border-border p-8 rounded-xl hover:border-primary transition-colors">
              <Heart className="text-primary mx-auto mb-4" size={48} />
              <h3 className="font-bold text-xl mb-3">Attend & Learn</h3>
              <ul className="text-sm text-muted-foreground text-left space-y-2 mb-6">
                <li>• Watch teams build AI solutions live</li>
                <li>• Learn at training tables and lightning talks</li>
                <li>• Network with mission-driven peers and potential employers</li>
              </ul>
              <Button className="w-full" onClick={scrollToRegistrationForm}>
                Register as Attendee
              </Button>
            </div>
            <div className="bg-card border border-border p-8 rounded-xl hover:border-secondary transition-colors">
              <Code className="text-secondary mx-auto mb-4" size={48} />
              <h3 className="font-bold text-xl mb-3">Build with a Team</h3>
              <ul className="text-sm text-muted-foreground text-left space-y-2 mb-6">
                <li>• Join a team or apply as an individual</li>
                <li>• Get coaching on AI and low/no-code tools</li>
                <li>• Work on a real nonprofit use case</li>
                <li>• <strong>Some AI experience is preferred, but not required</strong></li>
              </ul>
              <Button variant="outline" className="w-full" onClick={scrollToRegistrationForm}>
                Register as Participant
              </Button>
            </div>
            <div className="bg-card border border-border p-8 rounded-xl hover:border-primary transition-colors">
              <Users className="text-primary mx-auto mb-4" size={48} />
              <h3 className="font-bold text-xl mb-3">Support & Showcase</h3>
              <ul className="text-sm text-muted-foreground text-left space-y-2 mb-6">
                <li>• Provide financial support for production and tools</li>
                <li>• Get brand exposure before, during, and after</li>
                <li>• Connect with values-aligned talent</li>
              </ul>
              <Button variant="outline" className="w-full" onClick={scrollToRegistrationForm}>
                Become a Sponsor
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-8 max-w-3xl mx-auto">
            <strong>Childcare availability:</strong> During registration, you can indicate whether you will bring children and if you are interested in on-site childcare and kids' AI activities. Space may be limited.
          </p>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 px-4">
        <div className="max-w-5xl mx-auto space-y-12">
          <div className="text-center space-y-6">
            <h2 className="text-3xl md:text-5xl font-bold font-display">What the Day Feels Like</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Picture the energy of a live show, the focus of a hackathon, and the warmth of a community gathering — all centered on building tools that serve others.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Welcome & Story Setup", desc: "We introduce the cause and nonprofits we're serving, and share the stories behind the problems we're solving." },
              { title: "Team Intros & Kickoff", desc: "Teams are introduced like players on game day, with lights, music, and mission statements on the big screen." },
              { title: "Live Building + Training Tables", desc: "Teams build in real time while beginners and observers learn at training tables focused on AI basics, content, automation, and more." },
              { title: "Networking & Connections", desc: "Meet peers, mentors, nonprofit leaders, and potential employers. Build meaningful relationships and explore new opportunities." },
              { title: "Lightning Talks", desc: "Experts give short, high-impact talks to demystify AI and show practical workflows you can apply immediately." },
              { title: "Showcase & Awards", desc: "Teams present their solutions. Awards celebrate usefulness, creativity, visibility, and teamwork — not cutthroat competition." }
            ].map((item, idx) => (
              <div key={idx} className="bg-card border border-border p-6 rounded-xl">
                <h4 className="font-bold text-lg mb-3 text-primary">{item.title}</h4>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Teams & Participants Section */}
      <section id="teams" className="py-20 px-4 bg-card/30">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-5xl font-bold font-display">Who Participates?</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            BuildWithChrist.ai brings together a diverse community of builders, learners, and mission-driven professionals.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
            {[
              "Developers & Engineers",
              "Data & AI Professionals",
              "Product & Operations",
              "Marketing & Communications",
              "Students & Early-Career",
              "Nonprofit Liaisons"
            ].map((role, idx) => (
              <div key={idx} className="bg-card border border-border p-4 rounded-lg">
                <p className="text-sm font-medium">{role}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 p-6 bg-primary/10 border border-primary/20 rounded-xl max-w-2xl mx-auto">
            <p className="text-lg font-semibold text-foreground">
              Some AI experience is preferred for team participants, but not required.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Attendees need no AI experience—our goal is to teach everyone who comes. We'll have training tables, mentors, and starter workflows so that beginners can meaningfully contribute alongside more experienced builders.
            </p>
          </div>
        </div>
      </section>

      {/* Family-Friendly Section */}
      <section id="family" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-5xl font-bold font-display">Bring the Whole Family</h2>
              <p className="text-lg text-muted-foreground">
                BuildWithChrist.ai is intentionally family-friendly.
              </p>
              <p className="text-lg text-muted-foreground">
                Our event venue is connected to an indoor jungle gym and play area staffed by licensed childcare providers. Parents can focus on building, learning, and networking while their kids play safely next door.
              </p>
              <p className="text-lg text-muted-foreground">
                We may also offer optional kids' AI learning activities. During registration, you can indicate whether you're interested in AI learning sessions for your children so we can gauge demand.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Heart className="text-primary flex-shrink-0 mt-1" size={20} />
                  <span className="text-muted-foreground">Safe, supervised environment for children</span>
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="text-primary flex-shrink-0 mt-1" size={20} />
                  <span className="text-muted-foreground">Optional age-appropriate AI learning</span>
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="text-primary flex-shrink-0 mt-1" size={20} />
                  <span className="text-muted-foreground">Easy to attend as a parent — no need to choose between family and formation</span>
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <div className="relative h-[400px] bg-gradient-to-br from-secondary/20 to-primary/20 rounded-2xl border border-border overflow-hidden">
                <img 
                  src="/assets/childcare-placeholder.jpg" 
                  alt="Indoor supervised play area for children"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-sm text-muted-foreground text-center">
                Indoor supervised play area available for kids during the event.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Venue Section */}
      <section id="venue" className="py-20 px-4 bg-card/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center space-y-6 mb-12">
            <h2 className="text-3xl md:text-5xl font-bold font-display">The Venue</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              A theater-like, broadcast-ready environment with state-of-the-art LED walls and professional audio-video equipment.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="space-y-4">
              <div className="relative h-[300px] bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl border border-border overflow-hidden">
                <img 
                  src="/assets/heritage-hall.svg" 
                  alt="Phase Events Heritage Hall with LED wall and professional stage"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-sm text-muted-foreground text-center">
                Heritage Hall featuring state-of-the-art LED walls and theatrical production
              </p>
            </div>
            <div className="space-y-4">
              <div className="relative h-[300px] bg-gradient-to-br from-secondary/20 to-primary/20 rounded-2xl border border-border overflow-hidden">
                <img 
                  src="/assets/Phase_Events_Commons.jpg" 
                  alt="Phase Events training rooms and breakout spaces"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-sm text-muted-foreground text-center">
                Training rooms and flexible spaces for breakout sessions and learning
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-card border border-border p-6 rounded-xl">
              <h3 className="font-bold text-xl mb-4 text-primary">Location</h3>
              <div className="space-y-3 text-muted-foreground">
                <p className="font-semibold text-foreground">Phase Events – Heritage Hall</p>
                <p>12150 Morris Rd</p>
                <p>Alpharetta, GA 30005</p>
                <p className="text-sm mt-4">Near Avalon</p>
                <a href="tel:470-239-2482" className="text-primary hover:underline text-sm block mt-2">
                  470-239-2482
                </a>
              </div>
            </div>

            <div className="bg-card border border-border p-6 rounded-xl">
              <h3 className="font-bold text-xl mb-4 text-primary">Venue Features</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>State-of-the-art theater with LED walls and professional stage</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Training rooms and breakout spaces for hands-on learning</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Professional audio-video equipment for presentations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Connected to indoor play area and childcare facilities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Ample parking and accessible location</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      <section id="sponsors" className="py-20 px-4 bg-card/30">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-5xl font-bold font-display">Sponsor This Event</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Partner with us to make this event possible. Sponsorship opportunities include brand visibility, networking access, and connection with values-aligned talent.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
            {[
              { tier: "Bronze", color: "text-amber-600" },
              { tier: "Silver", color: "text-slate-400" },
              { tier: "Gold", color: "text-yellow-500" },
              { tier: "Platinum", color: "text-blue-400" }
            ].map((sponsor, idx) => (
              <div key={idx} className="bg-card border border-border p-6 rounded-xl">
                <h3 className={`font-bold text-xl mb-2 ${sponsor.color}`}>{sponsor.tier}</h3>
                <p className="text-sm text-muted-foreground">Tier benefits coming soon</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button size="lg">Download Sponsor Overview</Button>
            <Button size="lg" variant="outline">Talk to Us About Sponsorship</Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 bg-card/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-12 font-display">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((item, idx) => (
              <details key={idx} className="bg-card border border-border rounded-lg p-4 cursor-pointer group">
                <summary className="font-semibold flex justify-between items-center">
                  {item.q}
                  <ChevronDown className="group-open:rotate-180 transition-transform" size={20} />
                </summary>
                <p className="mt-3 text-muted-foreground">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
